import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  Grid,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import {
  CloudSync,
  CheckCircle,
  Error,
  Sync,
  Cloud,
  Assessment,
} from '@mui/icons-material';
import * as api from '../services/api';

interface SyncStats {
  progetti_sincronizzati: number;
  ultima_modifica: string;
}

interface LastSync {
  last_sync_timestamp: string;
  formatted: string;
}

interface SyncResult {
  success: boolean;
  action?: 'created' | 'updated';
  message?: string;
  total?: number;
  created?: number;
  updated?: number;
  errors?: any[];
  progetto?: any;
  hrp_data?: any;
}

interface PreviewProgetto {
  id_hrp: number;
  nome: string;
  commerciale: string;
  pm: string;
  cliente_finale: string;
  budget_gg_uu: number;
  data_inizio: string;
  data_fine: string;
  last_update: string;
  action: 'create' | 'update';
  myplanner_id: number | null;
  myplanner_titolo: string | null;
}

interface PreviewResult {
  total_count: number;
  preview_count: number;
  progetti: PreviewProgetto[];
  last_sync_timestamp: string;
  note: string | null;
}

const SyncProgettiPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  const [stats, setStats] = useState<SyncStats | null>(null);
  const [syncResult, setSyncResult] = useState<SyncResult | null>(null);
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [lastSync, setLastSync] = useState<LastSync | null>(null);
  const [hrpId, setHrpId] = useState<string>('');

  // Carica statistiche e ultimo sync all'avvio
  useEffect(() => {
    loadStats();
    loadLastSync();
  }, []);

  const loadStats = async () => {
    try {
      const data = await api.getSyncStats();
      setStats(data);
    } catch (error: any) {
      console.error('Errore caricamento statistiche:', error);
    }
  };

  const loadLastSync = async () => {
    try {
      const data = await api.getLastSync();
      setLastSync(data);
    } catch (error: any) {
      console.error('Errore caricamento last sync:', error);
    }
  };

  const testConnection = async () => {
    setTestingConnection(true);
    setConnectionStatus(null);
    try {
      const data = await api.testSyncConnection();
      setConnectionStatus({
        success: data.success,
        message: data.message,
      });
    } catch (error: any) {
      setConnectionStatus({
        success: false,
        message: error.response?.data?.error || 'Errore durante il test di connessione',
      });
    } finally {
      setTestingConnection(false);
    }
  };

  const syncSingleProject = async () => {
    if (!hrpId) {
      alert('Inserisci un ID HRP valido');
      return;
    }

    setLoading(true);
    setSyncResult(null);
    try {
      const data = await api.syncSingleProject(parseInt(hrpId));
      setSyncResult(data);
      setHrpId('');
      await loadStats();
    } catch (error: any) {
      setSyncResult({
        success: false,
        message: error.response?.data?.error || 'Errore durante la sincronizzazione',
      });
    } finally {
      setLoading(false);
    }
  };

  const loadPreview = async () => {
    setLoadingPreview(true);
    setPreview(null);
    try {
      const data = await api.syncPreview();
      setPreview(data);
    } catch (error: any) {
      alert(error.response?.data?.error || 'Errore durante il caricamento della preview');
    } finally {
      setLoadingPreview(false);
    }
  };

  const syncAllProjects = async () => {
    if (!preview) {
      alert('Carica prima la preview dei progetti da sincronizzare');
      return;
    }

    const confirm = window.confirm(
      `Confermi la sincronizzazione di ${preview.total_count} progetti da HRP?`
    );

    if (!confirm) return;

    setLoading(true);
    setSyncResult(null);
    try {
      const data = await api.syncAllProjects();
      setSyncResult(data);
      await loadStats();
      await loadLastSync();
      setPreview(null); // Reset preview dopo sync
    } catch (error: any) {
      setSyncResult({
        success: false,
        message: error.response?.data?.error || 'Errore durante la sincronizzazione',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CloudSync /> Sincronizzazione Progetti HRP
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Sincronizza i progetti da HRP (MariaDB) verso HarpaPlanner (PostgreSQL)
        </Typography>
      </Box>

      {/* Statistiche */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                Progetti Sincronizzati in HarpaPlanner
              </Typography>
              <Typography variant="h4" color="primary">
                {stats?.progetti_sincronizzati || 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Progetti con ID HRP valorizzato
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                Ultima Modifica
              </Typography>
              <Typography variant="h6">
                {stats?.ultima_modifica
                  ? new Date(stats.ultima_modifica).toLocaleString('it-IT')
                  : 'N/A'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                Ultimo Sync HRP
              </Typography>
              <Typography variant="h6">
                {lastSync?.formatted || 'Caricamento...'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Prossimo sync: progetti modificati dopo questa data
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Test Connessione */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Cloud /> Test Connessione HRP
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Verifica la connessione al database HRP (192.168.9.28:3306)
        </Typography>
        <Button
          variant="contained"
          onClick={testConnection}
          disabled={testingConnection}
          startIcon={testingConnection ? <CircularProgress size={20} /> : <Cloud />}
        >
          {testingConnection ? 'Testing...' : 'Test Connessione'}
        </Button>

        {connectionStatus && (
          <Alert severity={connectionStatus.success ? 'success' : 'error'} sx={{ mt: 2 }}>
            {connectionStatus.message}
          </Alert>
        )}
      </Paper>

      {/* Sincronizza Singolo Progetto */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Sync /> Sincronizza Singolo Progetto
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Inserisci l'ID HRP del progetto da sincronizzare. Se il progetto esiste già, verrà aggiornato.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            label="ID HRP Progetto"
            type="number"
            value={hrpId}
            onChange={(e) => setHrpId(e.target.value)}
            disabled={loading}
            size="small"
            sx={{ width: 200 }}
            helperText="Es: 12345"
          />
          <Button
            variant="contained"
            onClick={syncSingleProject}
            disabled={loading || !hrpId}
            startIcon={loading ? <CircularProgress size={20} /> : <Sync />}
          >
            {loading ? 'Sincronizzazione...' : 'Sincronizza'}
          </Button>
        </Box>
      </Paper>

      {/* Sincronizza Tutti i Progetti */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CloudSync /> Sincronizza Tutti i Progetti
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Sincronizza tutti i progetti da HRP che soddisfano i criteri (flag_ordine = 1, stato_chiuso_id NOT IN 1,2,3).
          La sincronizzazione è incrementale: verranno elaborati solo i progetti modificati dopo l'ultimo sync.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
          <Button
            variant="outlined"
            color="primary"
            onClick={loadPreview}
            disabled={loadingPreview || loading}
            startIcon={loadingPreview ? <CircularProgress size={20} /> : <Assessment />}
          >
            {loadingPreview ? 'Caricamento...' : 'Carica Preview'}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={syncAllProjects}
            disabled={loading || !preview}
            startIcon={loading ? <CircularProgress size={20} /> : <CloudSync />}
          >
            {loading ? 'Sincronizzazione...' : 'Sincronizza Tutti'}
          </Button>
        </Box>
        {lastSync && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>Sincronizzazione Incrementale Attiva:</strong> Verranno sincronizzati solo i progetti HRP modificati dopo il <strong>{lastSync.formatted}</strong>
          </Alert>
        )}

        {/* Preview Table */}
        {preview && (
          <Box sx={{ mt: 3 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>Trovati {preview.total_count} progetti</strong>
              {preview.note && ` - ${preview.note}`}
              {preview.last_sync_timestamp && ` - Modificati dopo: ${new Date(preview.last_sync_timestamp).toLocaleString('it-IT')}`}
            </Alert>
            <Box sx={{ maxHeight: 400, overflow: 'auto', border: '1px solid #ddd', borderRadius: 1 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f5f5f5' }}>
                  <tr>
                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>ID HRP</th>
                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Nome</th>
                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Commerciale</th>
                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>PM</th>
                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Cliente</th>
                    <th style={{ padding: '8px', textAlign: 'right', borderBottom: '2px solid #ddd' }}>Budget gg</th>
                    <th style={{ padding: '8px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Azione</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.progetti.map((p) => (
                    <tr key={p.id_hrp} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '8px' }}>{p.id_hrp}</td>
                      <td style={{ padding: '8px' }}>{p.nome}</td>
                      <td style={{ padding: '8px' }}>{p.commerciale || '-'}</td>
                      <td style={{ padding: '8px' }}>{p.pm || '-'}</td>
                      <td style={{ padding: '8px' }}>{p.cliente_finale || '-'}</td>
                      <td style={{ padding: '8px', textAlign: 'right' }}>{p.budget_gg_uu || 0}</td>
                      <td style={{ padding: '8px', textAlign: 'center' }}>
                        <Chip
                          label={p.action === 'create' ? 'NUOVO' : 'AGGIORNA'}
                          color={p.action === 'create' ? 'success' : 'info'}
                          size="small"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          </Box>
        )}
      </Paper>

      {/* Risultato Sincronizzazione */}
      {syncResult && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Risultato Sincronizzazione
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {syncResult.success ? (
            <Alert severity="success" icon={<CheckCircle />} sx={{ mb: 2 }}>
              {syncResult.message || 'Sincronizzazione completata con successo'}
            </Alert>
          ) : (
            <Alert severity="error" icon={<Error />} sx={{ mb: 2 }}>
              {syncResult.message || 'Errore durante la sincronizzazione'}
            </Alert>
          )}

          {syncResult.action && (
            <Box sx={{ mb: 2 }}>
              <Chip
                label={syncResult.action === 'created' ? 'PROGETTO CREATO' : 'PROGETTO AGGIORNATO'}
                color={syncResult.action === 'created' ? 'success' : 'info'}
                sx={{ mr: 1 }}
              />
            </Box>
          )}

          {syncResult.total !== undefined && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body1" gutterBottom>
                <strong>Totale progetti elaborati:</strong> {syncResult.total}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Creati:</strong> {syncResult.created || 0}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Aggiornati:</strong> {syncResult.updated || 0}
              </Typography>
            </Box>
          )}

          {syncResult.errors && syncResult.errors.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="error" gutterBottom>
                <strong>Errori riscontrati ({syncResult.errors.length}):</strong>
              </Typography>
              <Box component="pre" sx={{ fontSize: 12, overflow: 'auto', maxHeight: 200 }}>
                {JSON.stringify(syncResult.errors, null, 2)}
              </Box>
            </Box>
          )}

          {syncResult.progetto && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" gutterBottom>
                <strong>Dettagli progetto sincronizzato:</strong>
              </Typography>
              <Box component="pre" sx={{ fontSize: 12, overflow: 'auto', maxHeight: 200, backgroundColor: '#f5f5f5', p: 1, borderRadius: 1 }}>
                {JSON.stringify(syncResult.progetto, null, 2)}
              </Box>
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default SyncProgettiPage;
